import { ref } from 'vue';

/**
 * 5 storefront themes — navigation uses `secondary` (colored bar, white links in global header SCSS).
 * Accents/highlights: `accent`, `secondaryLight` for buttons, hovers, badges, prices.
 */
export const FRONTEND_THEME_STORAGE_KEY = 'laser-frontend-theme';

export const currentFrontendThemeId = ref('gold');

/**
 * @typedef {{
 *   id: string,
 *   label: string,
 *   accent: string,
 *   accentHover: string,
 *   accentSoft: string,
 *   secondary: string,
 *   secondaryLight: string,
 *   textOnLight: string,
 *   textMutedOnLight: string,
 *   borderOnLight: string,
 * }} FrontendTheme
 */

/** @type {Record<string, FrontendTheme>} */
export const FRONTEND_THEMES = {
  gold: {
    id: 'gold',
    label: 'Classic market',
    accent: '#fcbe00',
    accentHover: '#e5ac00',
    accentSoft: 'rgba(252, 190, 0, 0.14)',
    secondary: '#263C97',
    secondaryLight: '#0068c9',
    textOnLight: '#3d4a5c',
    textMutedOnLight: '#6b7688',
    borderOnLight: 'rgba(38, 60, 151, 0.2)',
  },
  daraz: {
    id: 'daraz',
    label: 'Daraz orange',
    accent: '#f85606',
    accentHover: '#e04d04',
    accentSoft: 'rgba(248, 86, 6, 0.15)',
    secondary: '#232f3e',
    secondaryLight: '#ff8f42',
    textOnLight: '#3f3d3a',
    textMutedOnLight: '#7a726c',
    borderOnLight: 'rgba(35, 47, 62, 0.2)',
  },
  shein: {
    id: 'shein',
    label: 'Shein rose',
    accent: '#ff2863',
    accentHover: '#e31f56',
    accentSoft: 'rgba(255, 40, 99, 0.14)',
    secondary: '#3d2f45',
    secondaryLight: '#ff6b90',
    textOnLight: '#4a3d4f',
    textMutedOnLight: '#7d6f82',
    borderOnLight: 'rgba(61, 47, 69, 0.22)',
  },
  ocean: {
    id: 'ocean',
    label: 'Ocean teal',
    accent: '#14b8a6',
    accentHover: '#0d9488',
    accentSoft: 'rgba(20, 184, 166, 0.14)',
    secondary: '#115e59',
    secondaryLight: '#38bdf8',
    textOnLight: '#334155',
    textMutedOnLight: '#647896',
    borderOnLight: 'rgba(17, 94, 89, 0.22)',
  },
  luxe: {
    id: 'luxe',
    label: 'Luxe purple',
    accent: '#a855f7',
    accentHover: '#9333ea',
    accentSoft: 'rgba(168, 85, 247, 0.14)',
    secondary: '#5b21b6',
    secondaryLight: '#a78bfa',
    textOnLight: '#3e3a66',
    textMutedOnLight: '#6f6b92',
    borderOnLight: 'rgba(91, 33, 182, 0.2)',
  },

  /*
   * Single-color themes — only ONE accent color is used across nav,
   * buttons, links and highlights. Background stays white and body text
   * stays in the standard near-black tones for natural readability.
   */
  blue: {
    id: 'blue',
    label: 'Pure blue',
    accent: '#2563eb',
    accentHover: '#1d4ed8',
    accentSoft: 'rgba(37, 99, 235, 0.14)',
    secondary: '#2563eb',
    secondaryLight: '#60a5fa',
    textOnLight: '#1f2937',
    textMutedOnLight: '#6b7280',
    borderOnLight: 'rgba(37, 99, 235, 0.22)',
  },
  yellow: {
    id: 'yellow',
    label: 'Pure yellow',
    accent: '#eab308',
    accentHover: '#ca8a04',
    accentSoft: 'rgba(234, 179, 8, 0.18)',
    secondary: '#eab308',
    secondaryLight: '#facc15',
    textOnLight: '#1f2937',
    textMutedOnLight: '#6b7280',
    borderOnLight: 'rgba(234, 179, 8, 0.28)',
  },
  orange: {
    id: 'orange',
    label: 'Pure orange',
    accent: '#ea580c',
    accentHover: '#c2410c',
    accentSoft: 'rgba(234, 88, 12, 0.14)',
    secondary: '#ea580c',
    secondaryLight: '#fb923c',
    textOnLight: '#1f2937',
    textMutedOnLight: '#6b7280',
    borderOnLight: 'rgba(234, 88, 12, 0.22)',
  },
  red: {
    id: 'red',
    label: 'Pure red',
    accent: '#dc2626',
    accentHover: '#b91c1c',
    accentSoft: 'rgba(220, 38, 38, 0.14)',
    secondary: '#dc2626',
    secondaryLight: '#f87171',
    textOnLight: '#1f2937',
    textMutedOnLight: '#6b7280',
    borderOnLight: 'rgba(220, 38, 38, 0.22)',
  },
};

export function applyFrontendTheme(themeId) {
  const t = FRONTEND_THEMES[themeId] || FRONTEND_THEMES.gold;
  const el = document.documentElement;
  el.style.setProperty('--fe-accent', t.accent);
  el.style.setProperty('--fe-accent-hover', t.accentHover);
  el.style.setProperty('--fe-accent-soft', t.accentSoft);
  el.style.setProperty('--fe-secondary', t.secondary);
  el.style.setProperty('--fe-secondary-light', t.secondaryLight);
  el.style.setProperty('--fe-text-on-light', t.textOnLight);
  el.style.setProperty('--fe-text-muted-on-light', t.textMutedOnLight);
  el.style.setProperty('--fe-border-on-light', t.borderOnLight);
  try {
    localStorage.setItem(FRONTEND_THEME_STORAGE_KEY, t.id);
  } catch (e) {
    /* ignore */
  }
  currentFrontendThemeId.value = t.id;
  return t.id;
}

export function hydrateFrontendTheme() {
  let id = 'gold';
  try {
    const s = localStorage.getItem(FRONTEND_THEME_STORAGE_KEY);
    if (s && FRONTEND_THEMES[s]) id = s;
  } catch (e) {
    /* ignore */
  }
  return applyFrontendTheme(id);
}